import type { Ref } from 'vue';
import { computed } from 'vue';

import type { FormGroup, FormSchema } from '@modules/forms/components/VForm/types';
import type { IFormSelectInputOption } from '@modules/forms/types';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';

import type { INoneNegativeCarryoverForm } from '../types';
import { TRANSLATION_GROUP_AFFILIATES_NONE_NEGATIVE_CARRYOVER } from '../constants';

export interface INoneNegativeCarryoverFormComposableOptions {
  merchantNameValue?: string;
  enableNncoValue?: boolean;
  dateValue?: Date;
}

export interface INoneNegativeCarryoverFormComposable {
  formSchema: Ref<FormSchema<INoneNegativeCarryoverForm>>;
  formGroups: Ref<FormGroup<INoneNegativeCarryoverForm>[]>;
}

export default function useNoneNegativeCarryoverForm(
  options: Ref<INoneNegativeCarryoverFormComposableOptions>,
): INoneNegativeCarryoverFormComposable {
  const { getTranslation } = useI18n();

  const enableNncoOptions = computed<IFormSelectInputOption<boolean>[]>(() => [
    {
      label: getTranslation(TRANSLATION_GROUP_AFFILIATES_NONE_NEGATIVE_CARRYOVER, 'ENABLE_NNCO_YES').value,
      value: true,
    },
    {
      label: getTranslation(TRANSLATION_GROUP_AFFILIATES_NONE_NEGATIVE_CARRYOVER, 'ENABLE_NNCO_NO').value,
      value: false,
    },
  ]);

  const formSchema = computed<FormSchema<INoneNegativeCarryoverForm>>(() => {
    const schema: FormSchema<INoneNegativeCarryoverForm> = {
      enable_nnco: {
        type: EFormInputTypes.SELECT,
        label: getTranslation(TRANSLATION_GROUP_AFFILIATES_NONE_NEGATIVE_CARRYOVER, 'ENABLE_NNCO_LABEL').value,
        options: enableNncoOptions.value,
        value: options.value?.enableNncoValue ?? false,
        required: true,
      },
      date: {
        type: EFormInputTypes.DATE,
        label: getTranslation(TRANSLATION_GROUP_AFFILIATES_NONE_NEGATIVE_CARRYOVER, 'DATE_LABEL').value,
        value: options.value?.dateValue,
        required: true,
        view: 'month',
        dateFormat: 'mm.yy',
      },
    };

    if (options.value?.merchantNameValue) {
      schema.merchant_name = {
        type: EFormInputTypes.TEXT,
        label: getTranslation(TRANSLATION_GROUP_AFFILIATES_NONE_NEGATIVE_CARRYOVER, 'MERCHANT_NAME_LABEL').value,
        value: options.value?.merchantNameValue,
        readonly: true,
      };
    }

    return schema;
  });

  const formGroups = computed<FormGroup<INoneNegativeCarryoverForm>[]>(() => ([
    {
      items: options.value?.merchantNameValue
        ? [
            'merchant_name',
            'enable_nnco',
            'date',
          ]
        : [
            'enable_nnco',
            'date',
          ],
    },
  ]));

  return {
    formSchema,
    formGroups,
  };
}
